import { Box, Typography, Drawer, IconButton, Stack } from '@mui/material';
import XmarkIcon from '@/app/assets/icons/xmark.svg?react';
import CheckedIcon from '@/app/assets/icons/checked.svg?react';
import NotificationSidebarItem from './ui/notification-sidebar-item';
import {
  GetNotificationsResponse,
  NotificationDataItem,
} from '@/app/api/types';
import { useViewAllNotificationsMutation } from '@/app/api';
import { useTranslation } from 'react-i18next';
import { BaseModalRef } from '@/app/helpers/types';
import NewRequestModal from '@/shared/modal/NewRequestModal/NewRequestModal';
import { useCallback, useRef } from 'react';
import { Virtuoso } from 'react-virtuoso';

interface NotificationSidebarProps {
  open: boolean;
  onClose: () => void;
  notifications?: GetNotificationsResponse;
}

export const NotificationSidebar = ({
  open,
  onClose,
  notifications,
}: NotificationSidebarProps) => {
  const { t } = useTranslation();
  const [viewAllNotifications] = useViewAllNotificationsMutation();
  const newRequestModalRef = useRef<BaseModalRef>(null);

  const unviewedCount = notifications?.totalUnviewedNotifications ?? 0;
  const items = notifications?.data;

  const handleViewAllNotifications = () => {
    viewAllNotifications();
    onClose();
  };

  const handleOpenNewRequest = useCallback(() => {
    onClose();
    newRequestModalRef.current?.open();
  }, [onClose]);

  const renderNotification = useCallback(
    (_index: number, notification: NotificationDataItem) => (
      <NotificationSidebarItem
        data={notification}
        onClose={onClose}
        onOpenNewRequest={handleOpenNewRequest}
      />
    ),
    [onClose, handleOpenNewRequest]
  );

  return (
    <>
      <Drawer anchor='right' open={open} onClose={onClose}>
        <Box
          sx={{
            width: 480,
            height: '100%',
            p: 3,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Stack
            direction='row'
            justifyContent='space-between'
            alignItems='center'
            flexShrink={0}
          >
            <Stack direction='row' alignItems='center' spacing={1}>
              <Typography sx={{ fontSize: 17, fontWeight: 600 }}>
                {t('notifications')}
              </Typography>
              {unviewedCount > 0 && (
                <Box
                  sx={{
                    width: 20,
                    height: 20,
                    background: '#F34F4F',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography
                    sx={{ fontSize: 11, fontWeight: 600, color: '#FFFFFF' }}
                  >
                    {unviewedCount}
                  </Typography>
                </Box>
              )}
            </Stack>

            <IconButton onClick={onClose}>
              <XmarkIcon />
            </IconButton>
          </Stack>

          {unviewedCount > 0 && (
            <Stack
              direction='row'
              alignItems='center'
              spacing={0.5}
              flexShrink={0}
              sx={{ cursor: 'pointer' }}
              onClick={handleViewAllNotifications}
            >
              <Box component={CheckedIcon} />
              <Typography
                sx={{ fontSize: 12, fontWeight: 500, color: 'primary.main' }}
              >
                {t('view_all_notifications')}
              </Typography>
            </Stack>
          )}

          <Box sx={{ flex: 1, minHeight: 0, mt: 3 }}>
            <Virtuoso
              data={items}
              computeItemKey={(_index, notification) => notification.unique}
              itemContent={renderNotification}
              increaseViewportBy={200}
              style={{ height: '100%' }}
            />
          </Box>
        </Box>
      </Drawer>

      <NewRequestModal ref={newRequestModalRef} />
    </>
  );
};
